import { DesignerApplication, WorkExperience, Reference, ReferenceType, TotalRevenue } from "../../__generated__/types";

// Validation result interface
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

// Work Experience Validation
export const validateWorkExperience = (
  experience: (WorkExperience | any)[] | undefined | null
): ValidationResult => {
  const errors: string[] = [];
  
  if (!experience || experience.length === 0) {
    errors.push("At least one work experience entry is required");
    return { isValid: false, errors };
  }

  // Check for minimum 3 consecutive years of full-time work experience (any role)
  let totalConsecutiveYears = 0;
  let hasValidExperience = false;

  // Sort experiences by start date to check for consecutive periods
  const sortedExperience = [...experience].sort((a, b) => {
    const dateA = a.startDate ? new Date(a.startDate) : new Date(0);
    const dateB = b.startDate ? new Date(b.startDate) : new Date(0);
    return dateA.getTime() - dateB.getTime();
  });

  for (const exp of sortedExperience) {
    if (!exp.startDate || !exp.title || !exp.company) {
      continue;
    }

    // Accept any role - no specific title restrictions
    hasValidExperience = true;
    
    // Calculate years for this position
    const startDate = new Date(exp.startDate);
    const endDate = exp.endDate ? new Date(exp.endDate) : new Date();
    const yearsInRole = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
    
    if (yearsInRole > 0) {
      totalConsecutiveYears += yearsInRole;
    }
  }

  if (!hasValidExperience) {
    errors.push("At least one work experience entry is required");
  }

  if (totalConsecutiveYears < 3) {
    errors.push(`Minimum 3 consecutive years of work experience required. Current: ${totalConsecutiveYears.toFixed(1)} years`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Professional References Validation
export const validateProfessionalReferences = (
  references: (Reference | any)[] | undefined | null
): ValidationResult => {
  const errors: string[] = [];
  
  if (!references || references.length === 0) {
    errors.push("At least 4 professional references are required");
    return { isValid: false, errors };
  }

  const clientReferences = references.filter(ref => ref.type === ReferenceType.Client);
  const professionalReferences = references.filter(ref => ref.type !== ReferenceType.Client);

  if (clientReferences.length < 2) {
    errors.push(`Minimum 2 client references required. Current: ${clientReferences.length}`);
  }

  if (professionalReferences.length < 2) {
    errors.push(`Minimum 2 professional references required. Current: ${professionalReferences.length}`);
  }

  if (references.length < 4) {
    errors.push(`Minimum 4 total references required. Current: ${references.length}`);
  }

  // Validate that all references have required fields
  const incompleteReferences = references.filter(ref => 
    !ref.firstName || !ref.lastName || !ref.email || !ref.phoneNumber
  );

  if (incompleteReferences.length > 0) {
    errors.push(`${incompleteReferences.length} reference(s) have incomplete information`);
  }

  // Prevent duplicate references by email/phone (case/format insensitive)
  const normalizeEmail = (email?: string | null) => (email ?? "").trim().toLowerCase();
  const normalizePhone = (phone?: string | null) => (phone ?? "").replace(/\D/g, "");

  const duplicateEmails = Object.entries(
    references.reduce<Record<string, number>>((acc, ref) => {
      const emailKey = normalizeEmail(ref.email);
      if (!emailKey) return acc;
      acc[emailKey] = (acc[emailKey] ?? 0) + 1;
      return acc;
    }, {})
  )
    .filter(([, count]) => count > 1)
    .map(([email]) => email);

  if (duplicateEmails.length > 0) {
    errors.push(`Duplicate reference email(s) found: ${duplicateEmails.join(", ")}`);
  }

  const duplicatePhones = Object.entries(
    references.reduce<Record<string, number>>((acc, ref) => {
      const phoneKey = normalizePhone(ref.phoneNumber);
      if (!phoneKey) return acc;
      acc[phoneKey] = (acc[phoneKey] ?? 0) + 1;
      return acc;
    }, {})
  )
    .filter(([, count]) => count > 1)
    .map(([phone]) => phone);

  if (duplicatePhones.length > 0) {
    errors.push(`Duplicate phone number(s) found: ${duplicatePhones.join(", ")}`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Revenue Validation
export const validateRevenue = (
  application: Partial<DesignerApplication> | any
): ValidationResult => {
  const errors: string[] = [];
  
  const lastYearRevenue = application?.businessGoals?.overview?.totalRevenueLastYear;
  const currentYearRevenue = application?.businessGoals?.overview?.totalRevenueCurrentYear;

  // Debug logging

  // Check if either revenue meets the $150k minimum
  const meetsMinimum = 
    lastYearRevenue === TotalRevenue.OneHundredFiftyToTwoHundred ||
    lastYearRevenue === TotalRevenue.TwoHundredAndUp ||
    currentYearRevenue === TotalRevenue.OneHundredFiftyToTwoHundred ||
    currentYearRevenue === TotalRevenue.TwoHundredAndUp;


  if (!meetsMinimum) {
    errors.push("Minimum $150,000 in projected annual revenue or earned in the previous year is required. Please select '$150,000 - $200,000' or '$200,000+' for either last year or current year revenue.");
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Overall Application Validation
export const validateApplication = (
  application: Partial<DesignerApplication>
): ValidationResult => {
  const allErrors: string[] = [];
  
  // Validate work experience
  const workExpValidation = validateWorkExperience(application?.experience);
  if (!workExpValidation.isValid) {
    allErrors.push("Work Experience:", ...workExpValidation.errors);
  }

  // Validate references
  const refValidation = validateProfessionalReferences(application?.references);
  if (!refValidation.isValid) {
    allErrors.push("Professional References:", ...refValidation.errors);
  }

  // Validate revenue
  const revenueValidation = validateRevenue(application);
  if (!revenueValidation.isValid) {
    allErrors.push("Revenue Requirements:", ...revenueValidation.errors);
  }

  return {
    isValid: allErrors.length === 0,
    errors: allErrors
  };
};

// Legacy function for backward compatibility
export const isWorkExperienceValid = (
  application: Partial<DesignerApplication>
) => {
  const validation = validateWorkExperience(application?.experience);
  return validation.isValid;
};
